<template>
  <dialog-layout
    title="Round robin points"
    :fullscreen="fullscreen"
    :navIcon="navIcon"
    :dark="dark"
    @pop-dialog="$emit('pop-dialog')"
  >
    <v-container>
      <div style="height: 400px">
        <LineChart v-if="chartData" ref="chartComponent" :data="chartData" :options="chartOptions" />
      </div>
    </v-container>
  </dialog-layout>
</template>

<script>
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  CategoryScale
} from "chart.js";
import { DialogLayout } from "vue-app-components";
import { generateShades, chartClickMixin, baseChartOptions } from "@/utils/chart-utils";
import { pointsEarnedInRace } from "@/utils/league-points";

ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip, Legend, CategoryScale);

export default {
  name: "dialog-league-chart",
  mixins: [chartClickMixin],
  components: {
    DialogLayout,
    LineChart: Line
  },
  props: {
    fullscreen: { type: Boolean, required: true },
    navIcon: { type: String, required: true },
    dark: { type: Boolean, default: false }
  },
  inject: ["$store"],
  data () {
    return {
      chartData: null,
      chartOptions: {
        ...baseChartOptions,
        scales: {
          x: {
            type: "category",
            title: {
              display: true,
              text: "League races"
            }
          },
          y: {
            title: {
              display: true,
              text: "Points"
            },
            min: 0,
            stepSize: 1
          }
        },
        plugins: {
          ...baseChartOptions.plugins,
          tooltip: {
            ...baseChartOptions.plugins.tooltip,
            callbacks: {
              title: () => ""
            }
          }
        },
        onClick: (event, elements, chart) => {
          const nearest = chart.getElementsAtEventForMode(event, "nearest", { intersect: false }, false);
          this.handleChartClick(nearest);
        }
      }
    };
  },
  created () {
    this.$watch(() => this.$store.league?.races, () => {
      this.updateChartData();
    }, { immediate: true });
  },
  methods: {
    async updateChartData () {
      await this.loadInitialData();
      const league = this.$store.league;
      if (!league) return;
      const sortedRaces = [...league.races]
        .filter(r => r.decided && !r.isBracketRace)
        .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());

      if (sortedRaces.length === 0) {
        const colors = this.$vuetify.theme.current.colors;
        const secondaryColor = colors.secondary;
        const primaryColor = colors.primary;
        const myMember = league.members.find(m => m.memberUserId === this.$store.user.id);
        const mySquadId = myMember ? league.squads.find(s => s.memberIds.includes(myMember.id))?.id : null;
        const opponentSquads = league.squads.filter(s => s.id !== mySquadId);
        const opponentShades = generateShades(secondaryColor, opponentSquads.length);

        this.chartData = {
          labels: ["0"],
          datasets: league.squads.map(s => {
            const isCurrentUserSquad = s.id === mySquadId;
            const shadeIndex = opponentSquads.findIndex(os => os.id === s.id);
            const color = isCurrentUserSquad ? primaryColor : opponentShades[shadeIndex];
            return {
              label: s.title || "Unknown",
              data: [0],
              borderColor: color,
              backgroundColor: color,
              pointRadius: 3,
              tension: 0.1,
              borderWidth: isCurrentUserSquad ? 2 : 1.5,
              _isCurrentUser: isCurrentUserSquad
            };
          })
        };
        return;
      }

      const labels = ["0", ...sortedRaces.map((_, i) => String(i + 1))];
      const squadPoints = {};
      const squadHistory = {};
      const currentUserId = this.$store.user.id;
      const myMember = league.members.find(m => m.memberUserId === currentUserId);
      const mySquadId = myMember ? league.squads.find(s => s.memberIds.includes(myMember.id))?.id : null;

      league.squads.forEach(s => {
        if (sortedRaces.some(r => r.squadIds.includes(s.id))) {
          squadHistory[s.id] = [0];
        }
      });

      sortedRaces.forEach(race => {
        const participantIds = race.squadIds;
        participantIds.forEach(squadId => {
          if (!squadPoints[squadId]) squadPoints[squadId] = 0;
          squadPoints[squadId] += pointsEarnedInRace(race, squadId);
        });
        participantIds.forEach(squadId => {
          squadHistory[squadId].push(squadPoints[squadId] || 0);
        });
      });

      const datasets = [];
      const colors = this.$vuetify.theme.current.colors;
      const secondaryColor = colors.secondary;
      const primaryColor = colors.primary;
      const opponentSquads = league.squads.filter(s => s.id !== mySquadId);
      const opponentShades = generateShades(secondaryColor, opponentSquads.length);

      league.squads.forEach((s, i) => {
        const data = squadHistory[s.id];
        if (!data || data.length === 0) return;
        const isCurrentUserSquad = s.id === mySquadId;
        const shadeIndex = opponentSquads.findIndex(os => os.id === s.id);
        const color = isCurrentUserSquad ? primaryColor : opponentShades[shadeIndex];
        datasets.push({
          label: s.title || "Unknown",
          data,
          borderColor: color,
          backgroundColor: color,
          pointRadius: 3,
          tension: 0.1,
          borderWidth: isCurrentUserSquad ? 2 : 1.5,
          _isCurrentUser: isCurrentUserSquad
        });
      });

      this.chartData = { labels, datasets };
    },
    async loadInitialData () {
      const league = this.$store.league;
      if (!league) return;
      await Promise.all(league.races.map(r => r.promiseGames));
    },
    applySelectedStyles () {
      const chart = this.$refs.chartComponent?.chart;
      if (!chart) return;
      const datasets = chart.data.datasets;
      const selected = this.selectedDatasetIndex;
      datasets.forEach((ds, i) => {
        ds._originalColor ??= ds.borderColor;
        if (selected === null || i !== selected) {
          ds.borderWidth = ds._isCurrentUser ? 2 : 1.5;
          ds.borderColor = ds._originalColor;
          ds.backgroundColor = ds._originalColor;
          ds.pointRadius = 3;
        } else {
          ds.pointRadius = 4;
          if (ds._isCurrentUser) {
            ds.borderWidth = 3;
          } else {
            ds.borderWidth = 2;
            ds.borderColor = "#21356A";
          }
        }
      });
      chart.update();
    }
  }
};
</script>
